// Refresh Button Component
// Uniform refresh button matching ThemeToggle styling

import React from 'react';
import { useChatRoomTheme } from '../../contexts/ChatRoomThemeContext';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const RefreshButtonStyled = styled.button<{ $isDark: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${props => props.$isDark 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.05)'};
  border: 1px solid ${props => props.$isDark 
    ? 'rgba(255, 255, 255, 0.2)' 
    : 'rgba(0, 0, 0, 0.1)'};
  border-radius: ${theme.borderRadius.md};
  color: ${props => props.$isDark ? 'white' : '#333'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 100px;
  opacity: ${props => props.$disabled ? 0.6 : 1};

  &:hover:not(:disabled) {
    background: ${props => props.$isDark 
      ? 'rgba(255, 255, 255, 0.15)' 
      : 'rgba(0, 0, 0, 0.08)'};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: 0.8rem;
    min-width: 80px;
  }
`;

const Icon = styled.span`
  font-size: 1rem;
  line-height: 1;
`;

interface RefreshButtonProps {
  onClick: () => void;
  disabled?: boolean;
  isRefreshing?: boolean;
}

export const RefreshButton: React.FC<RefreshButtonProps> = ({ 
  onClick, 
  disabled = false,
  isRefreshing = false 
}) => {
  const { theme: chatRoomTheme } = useChatRoomTheme();
  const isDark = chatRoomTheme === 'dark';

  return (
    <RefreshButtonStyled 
      $isDark={isDark} 
      $disabled={disabled}
      onClick={onClick}
      disabled={disabled}
      aria-label="Refresh"
      title="Refresh"
    >
      <Icon>🔄</Icon>
      <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
    </RefreshButtonStyled>
  );
};

